import { useEffect, useRef } from 'react';

const useInfiniteTableScroll = ({
  containerRef,
  enabled = true,
  hasMore,
  loading,
  onLoadMore,
  threshold = 120,
}) => {
  const hasMoreRef = useRef(hasMore);
  const loadingRef = useRef(loading);
  const onLoadMoreRef = useRef(onLoadMore);

  hasMoreRef.current = hasMore;
  loadingRef.current = loading;
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (!enabled) return undefined;

    let bodyEl = null;
    let pollId = null;
    let lastScrollTop = 0;
    let lastGestureLoad = 0;
    let touchStartY = null;

    const loadFromGesture = () => {
      if (loadingRef.current || !hasMoreRef.current) return;
      if (bodyEl.scrollHeight > bodyEl.clientHeight + 1) return;
      const now = Date.now();
      if (now - lastGestureLoad < 800) return;
      lastGestureLoad = now;
      onLoadMoreRef.current?.();
    };

    const onWheel = (event) => {
      if (event.deltaY > 0) loadFromGesture();
    };

    const onTouchStart = (event) => {
      touchStartY = event.touches?.[0]?.clientY ?? null;
    };

    const onTouchMove = (event) => {
      const y = event.touches?.[0]?.clientY;
      if (touchStartY == null || y == null) return;
      if (touchStartY - y > 30) {
        touchStartY = null;
        loadFromGesture();
      }
    };

    const onScroll = () => {
      const movedDown = bodyEl.scrollTop > lastScrollTop;
      lastScrollTop = bodyEl.scrollTop;
      if (!movedDown) return;
      if (loadingRef.current || !hasMoreRef.current) return;
      if (bodyEl.scrollHeight <= bodyEl.clientHeight + 1) return;
      if (
        bodyEl.scrollTop + bodyEl.clientHeight >=
        bodyEl.scrollHeight - threshold
      ) {
        onLoadMoreRef.current?.();
      }
    };

    const attach = () => {
      const found = containerRef.current?.querySelector('.ant-table-body');
      if (!found) return false;
      bodyEl = found;
      bodyEl.addEventListener('scroll', onScroll, { passive: true });
      bodyEl.addEventListener('wheel', onWheel, { passive: true });
      bodyEl.addEventListener('touchstart', onTouchStart, { passive: true });
      bodyEl.addEventListener('touchmove', onTouchMove, { passive: true });
      return true;
    };

    if (!attach()) {
      pollId = setInterval(() => {
        if (attach()) {
          clearInterval(pollId);
          pollId = null;
        }
      }, 60);
    }

    return () => {
      if (pollId) clearInterval(pollId);
      if (bodyEl) {
        bodyEl.removeEventListener('scroll', onScroll);
        bodyEl.removeEventListener('wheel', onWheel);
        bodyEl.removeEventListener('touchstart', onTouchStart);
        bodyEl.removeEventListener('touchmove', onTouchMove);
      }
    };
  }, [containerRef, enabled, threshold]);
};

export default useInfiniteTableScroll;
